// src/components/RequiredInfoModal.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateUserSettings } from '../api/supabaseAPI';
import ModernDatePicker from './ModernDatePicker';
import ModernCombobox from './ModernCombobox';
import countries from '../list_data/countries_en.json';
import '../styles/RequiredInfoModal.css';

/**
 * RequiredInfoModal - A modal to collect required user information
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {object} props.userSettings - Current user settings for pre-filling
 * @param {string} props.userId - Current user's ID
 * @param {() => void} props.onConfirm - Callback after successful save
 * @param {() => void} props.onCancel - Callback when user cancels (navigates away)
 */
const RequiredInfoModal = React.memo(({ isOpen, userSettings, userId, onConfirm, onCancel }) => {
  const queryClient = useQueryClient();
  
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [birthday, setBirthday] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');

  // Pre-fill form with existing data if available
  useEffect(() => {
    if (userSettings) {
      setGeminiApiKey(userSettings.gemini_api_key || '');
      setBirthday(userSettings.birthday || '');
      setCountry(userSettings.country || '');
    }
  }, [userSettings]);

  // Convert countries array to options format for ModernCombobox
  const countryOptions = useMemo(() => {
    return countries.map((c) => ({
      value: c.code,
      label: c.name,
    }));
  }, []);

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return geminiApiKey.trim() !== '' && birthday !== '' && country !== '';
  }, [geminiApiKey, birthday, country]);

  // Mutation for saving user settings
  const { mutate: saveSettings, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      return updateUserSettings(userId, {
        gemini_api_key: geminiApiKey.trim(),
        birthday: birthday,
        country: country,
      });
    },
    onSuccess: () => {
      // Invalidate user query to refresh data
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      onConfirm();
    },
    onError: (err) => {
      setError(err.message || 'Failed to save settings. Please try again.');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!isFormValid) {
      setError('Please fill in all required fields.');
      return;
    }

    saveSettings();
  };

  const handleOverlayClick = (e) => {
    // Only trigger cancel if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="required-info-overlay" onClick={handleOverlayClick}>
      <div className="required-info-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="required-info-header">
          <h2 className="required-info-title">Complete Your Profile</h2>
          <button 
            type="button" 
            className="required-info-close" 
            onClick={onCancel}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="required-info-body">
          <p className="required-info-subtitle">
            Before starting the test, please provide the following information to personalize your experience.
          </p>

          {error && (
            <div className="required-info-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="required-info-form">
            {/* Gemini API Key */}
            <div className="input-group">
              <label htmlFor="gemini-api-key">Gemini API Key</label>
              <input
                type="password"
                id="gemini-api-key"
                value={geminiApiKey}
                onChange={(e) => setGeminiApiKey(e.target.value)}
                placeholder="Enter your Gemini API Key"
                autoComplete="off"
              />
              <p className="input-help-text">
                Get your free API key from{' '}
                <a 
                  href="https://aistudio.google.com/app/apikey" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Google AI Studio
                </a>
              </p>
            </div>

            {/* Birthday */}
            <ModernDatePicker
              label="Birthday"
              value={birthday}
              onChange={setBirthday}
              placeholder="Select your birthday"
            />

            {/* Country */}
            <ModernCombobox
              label="Country"
              options={countryOptions}
              value={country}
              onChange={setCountry}
              placeholder="-- Select your country --"
            />

            {/* Footer Actions */}
            <div className="required-info-actions">
              <button
                type="button"
                className="required-info-btn secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="required-info-btn primary"
                disabled={!isFormValid || isSaving}
              >
                {isSaving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
});

export default RequiredInfoModal;
